import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { StoreService } from 'src/app/core/services/store.service';
import { environment } from 'src/environments/environment';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { ShareModule } from 'ngx-sharebuttons';
import { LowerCasePipe, CurrencyPipe, CommonModule } from '@angular/common';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ReviewsComponent } from 'src/app/shared/components/reviews/reviews.component';
import { ProductGridComponent } from 'src/app/shared/components/product-grid/product-grid.component';
import { EmptyStoreComponent } from 'src/app/shared/components/empty-store/empty-store.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [
    HeaderComponent,
    ReviewsComponent,
    FooterComponent,
    RouterModule,
    CommonModule,
    ProductGridComponent,
    LoadingSpinnerComponent,
    EmptyStoreComponent,
    NgxPaginationModule,
    ShareModule,
  ],
})
export class HomeComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private snackBar = inject(SnackBarService);
  public store = inject(StoreService);

  singleStore!: { storename: string };
  storeResponse: any;
  storeCode = '';
  storeFetched = false;
  storeNotFound = false;
  deactivated = false;

  products: any[] = [];
  productFetched = false;
  productSearch = false;
  totalElements = 0;
  totalItems = 0;
  lastpage = 0;

  query = '';
  type = '';
  filter = false;

  readonly options = [
    'All categories',
    'High to Low price',
    'Low to High price',
  ];
  readonly marketplaceUrl = '';

  cart: any[] =
    JSON.parse(localStorage.getItem(this.store.getStoreCartKey('cart'))!) ?? [];

  showCartQuantity: { code: string; visible: boolean }[] = [];
  currentStore!: { store_code: any; store_name: any };

  ngOnInit(): void {
    this.storeFetched = false;
    this.singleStore = { storename: this.route.snapshot.params['name'] };

    const name = this.singleStore.storename;
    const currentStore =
      JSON.parse(sessionStorage.getItem('currentStore')!) ?? null;

    if (
      currentStore &&
      currentStore.store_name.toLowerCase() !== name.toLowerCase()
    ) {
      this.store.cartItems = [];
      localStorage.removeItem(this.store.getStoreCartKey('cart'));
      localStorage.removeItem(this.store.getStoreCartKey('cartTotal'));
    }

    this.getStore(name);
    window.scrollTo(0, 0);
  }

  getStore(name: string) {
    this.store.findStoreByName(name).subscribe({
      next: (response: any) => {
        this.storeResponse = response;

        if (!this.storeResponse.active) {
          this.deactivated = true;
          this.storeFetched = true;
          this.productFetched = true;
          this.storeNotFound = false;
          return;
        }
        this.storeFetched = true;

        this.storeCode = this.storeResponse.store_code;
        this.currentStore = {
          store_code: this.storeResponse.store_code,
          store_name: this.storeResponse.slug,
        };

        this.store.checkStoreCart(this.storeResponse.store_code);

        sessionStorage.setItem(
          'currentStore',
          JSON.stringify(this.currentStore),
        );
        this.productFetched = false;

        this.getAllProducts();
      },
      error: (err) => {
        this.storeNotFound = true;
        this.storeFetched = true;
        this.productFetched = true;
      },
    });
  }

  getAllProducts(): void {
    this.productFetched = false;
    // Reset search/filter context when reloading all products
    this.productSearch = false;
    this.query = '';
    this.type = '';

    this.store
      .getProductOnClient(this.storeCode, this.store.currentPage)
      .subscribe({
        next: (response: any) => {
          this.products = response.data;
          this.showCartQuantity = this.products.map((p) => ({
            code: p.product.product_code,
            visible: false,
          }));
          this.setPagination(response);
          this.productFetched = true;
        },
        error: () => {
          this.snackBar.openSnackBar('Unable to complete', 'error-snackbar');
          this.productFetched = true;
        },
      });
  }

  filterAndSearchProduct($event?: any): void {
    if ($event) {
      this.query = $event.target.value;
      this.store.currentPage = 1;
    }

    if (!this.query.trim()) {
      this.getAllProducts();
      return;
    }

    this.productFetched = false;
    this.store
      .productSearchAndFilter(
        this.query,
        this.storeCode,
        this.store.currentPage,
      )
      .subscribe({
        next: (response: any) => {
          if (!response.data?.length) {
            this.getAllProducts();
            this.snackBar.openSnackBar('No product found', 'error-snackbar');
            return;
          }
          this.products = response.data;
          this.productSearch = true;
          this.store.currentPage = 1;
          this.setPagination(response);
          this.productFetched = true;
        },
        error: (err) => {
          this.snackBar.openSnackBar(err.error?.message, 'error-snackbar');
          this.productFetched = true;
        },
      });
  }

  filterWithPrice($event?: any): void {
    if ($event) {
      this.store.currentPage = 1;
      const value = $event?.value ?? $event?.target?.value;
      console.log('Selected option in homepage for price filtering:', value);
      if (value === 'All categories') {
        this.getAllProducts();
        return;
      }
      this.type = value === 'High to Low price' ? 'desc' : 'asc';
    }

    this.productFetched = false;
    this.store
      .productFilterByPrice(this.type, this.store.currentPage)
      .subscribe({
        next: (response: any) => {
          this.products = response.data;
          this.setPagination(response);
          this.productFetched = true;
        },
        error: (err) => {
          this.snackBar.openSnackBar(err.error?.message, 'error-snackbar');
          this.productFetched = true;
        },
      });
  }

  changePage(pageNumber: number): void {
    this.store.currentPage = pageNumber;

    if (this.type) {
      this.filterWithPrice();
    } else if (this.query) {
      this.filterAndSearchProduct();
    } else {
      this.getAllProducts();
    }

    window.scrollTo(0, 0);
  }

  reset(): void {
    this.getAllProducts();
  }

  openFilter(): void {
    this.filter = !this.filter;
  }

  addToCart(event: { code: string; index: number }): void {
    const item = this.showCartQuantity.find((s) => s.code === event.code);
    if (item) item.visible = !item.visible;

    const cartProduct = this.products.find(
      (p) => p.product.product_code === event.code,
    );
    if (cartProduct) this.store.addCartItem(cartProduct);
  }

  // Helpers
  private setPagination(response: any): void {
    this.totalElements = response.totalElements;
    this.lastpage = response.totalPages;
    this.totalItems = this.totalElements * this.lastpage;
  }
}
